// "This weekend, for you": the pure rules (which weekend, what counts as a one-off, which five). No imports, so
// tests/growth_1016_test.ts runs them under Deno. The loading and keeping is lib/weekend.ts.
type CardLike = { item: { id: string; kind: string; title: string; venue_name: string | null; start_at: string | null; end_at: string | null } }
const TZ = 'America/Los_Angeles'
export const WEEKEND_SIZE = 5

export const ymdIn = (d: Date) => new Intl.DateTimeFormat('en-CA', { timeZone: TZ }).format(d)
const addDays = (ymd: string, n: number) => { const d = new Date(`${ymd}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10) }
const dowOf = (ymd: string) => new Date(`${ymd}T12:00:00Z`).getUTCDay()

export type WeekendWindow =
  /** Thursday to Sunday: the five are out. `from` is the first day still ahead (today on Fri-Sun), `fri` names the set. */
  | { open: true; fri: string; from: string; to: string }
  /** Monday to Wednesday: the next five arrive Thursday. */
  | { open: false; fri: string; thursday: string }

/** Which weekend the five are for, on `now` in LA. */
export function weekendWindow(now: Date): WeekendWindow {
  const today = ymdIn(now)
  const dow = dowOf(today) // 0 Sunday .. 6 Saturday
  if (dow === 4) return { open: true, fri: addDays(today, 1), from: addDays(today, 1), to: addDays(today, 3) }
  if (dow === 5 || dow === 6 || dow === 0) {
    const fri = addDays(today, dow === 5 ? 0 : dow === 6 ? -1 : -2)
    return { open: true, fri, from: today, to: addDays(fri, 2) }
  }
  const thursday = addDays(today, 4 - dow)
  return { open: false, fri: addDays(thursday, 1), thursday }
}

const HOUR = 3600e3
/** A one-off: a dated event no longer than a day and a half that falls inside this weekend (not a long run or a place). */
export function isOneOff(c: CardLike, from: string, to: string): boolean {
  const it = c.item
  if (it.kind !== 'event' || !it.start_at) return false
  const s = new Date(it.start_at).getTime(), e = new Date(it.end_at ?? it.start_at).getTime()
  if (!(e - s <= 36 * HOUR)) return false
  const sd = ymdIn(new Date(s)), ed = ymdIn(new Date(e))
  return sd >= from && ed <= to
}

/**
 * The five, from the For you list in taste order: one-offs first (up to four), then the best of the rest, then any
 * one-offs left; no two from the same venue. Ties keep the server's taste order.
 */
export function pickFive<T extends CardLike>(cards: T[], from: string, to: string, n = WEEKEND_SIZE): T[] {
  const seen = new Set<string>()
  const venue = (c: T) => (c.item.venue_name ?? c.item.title).toLowerCase()
  const fresh = cards.filter((c) => { const v = venue(c); if (seen.has(v)) return false; seen.add(v); return true })
  const one = fresh.filter((c) => isOneOff(c, from, to)), rest = fresh.filter((c) => !isOneOff(c, from, to))
  const out = one.slice(0, n - 1)
  for (const c of rest) { if (out.length >= n) break; out.push(c) }
  for (const c of one.slice(n - 1)) { if (out.length >= n) break; out.push(c) }
  // back in taste order, so the strongest pick leads whatever kind it is
  const rank = new Map(cards.map((c, i) => [c.item.id, i]))
  return out.sort((a, b) => rank.get(a.item.id)! - rank.get(b.item.id)!)
}

/** "Fri Oct 17 to Sun Oct 19" style range for the copy. */
export function rangeLabel(from: string, to: string): string {
  const f = (ymd: string) => new Date(`${ymd}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' })
  return from === to ? f(from) : `${f(from)} to ${f(to)}`
}
